import { useEffect } from "react";

//DEPENDENCIES
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

//UTILS
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  updateAccounts,
  updateAccountSelected,
} from "../store/slices/userAccountsSlice";
import { defaultQueryConfig } from "../utils";
import { getAccountBalance } from "../connectors/positionApi";
import {
  checkAllariaMasLinked,
  getUserAccounts,
} from "../store/slices/authSlice";
import { getFavoriteAccount } from "../utils/favoriteAccount";
import { getUnifyAccount } from "../connectors/unifiedBalanceApi";
import { fetchTermsAndConditionsStatus } from "../store/slices/termsAndConditionsSlice";

//TYPES
import { BalanceType, Currency, UserAccount } from "../types/types";

const useAccountData = () => {
  const navigate = useNavigate();
  const { pathname, hash, search } = useLocation();

  const queryParams = new URLSearchParams(search);
  const accountId = queryParams.get("accountId") ?? "";

  const dispatch = useAppDispatch();

  const { user, auth } = useAppSelector((state) => state.authentication);
  const { accountSelected, balanceRefetchTrigger } = useAppSelector(
    (state) => state.userAccounts,
  );
  const { hasAccepted } = useAppSelector((state) => state.termsAndConditions);

  const { data: userAccountsData, isPending: loadingUserAccountsData } =
    useQuery({
      enabled: !!user && !!auth?.access_token,
      queryFn: () => getUserAccounts(),
      queryKey: ["userAccounts"],
      select: (response) => {
        if (response?.esco && response.esco?.accounts) {
          const favoriteAccountNumber = user?.email
            ? getFavoriteAccount({ email: user.email })
            : null;

          return response.esco.accounts.map((acc) => ({
            name: acc.description,
            number: acc.id,
            isJuridical: !acc.is_natural_person,
            mirroredAccount: false,
            unifyState: "NOT_MIRRORED",
            inSinglAccount: response.esco?.accounts?.length === 1,
            isFavorite: favoriteAccountNumber == acc.id,
            isEnabled: acc.is_enabled,
            userType: response?.user_type,
            isEnabledOperate:
              acc.is_enabled && response?.user_type !== "COMMERCIAL",
          }));
        }
        return [];
      },
      ...defaultQueryConfig,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    });

  const { data: unifyAccountData, isPending: loadingUnifyAccount } = useQuery({
    enabled: !!user && !!auth?.access_token && !!accountSelected?.number,
    queryFn: () => getUnifyAccount({ accountNumber: accountSelected!.number }),
    queryKey: ["userAccountUnify", accountSelected?.number],
    ...defaultQueryConfig,
  });

  // Only fetch balance for selected account
  const { data: accountBalanceData, isLoading: loadingAccountBalanceData } =
    useQuery({
      enabled: !!accountSelected?.number,
      queryFn: () =>
        getAccountBalance({
          accountNumber: accountSelected!.number,
        }),
      queryKey: [
        "accountBalancePositions",
        accountSelected?.number,
        balanceRefetchTrigger,
      ],
      ...defaultQueryConfig,
    });

  useEffect(() => {
    if (!user || !auth?.access_token) return;
    if (hasAccepted !== null) return;

    dispatch(fetchTermsAndConditionsStatus());
  }, [user?.email, auth?.access_token]);

  useEffect(() => {
    if (!loadingUserAccountsData && userAccountsData) {
      const sortedAccounts = [...userAccountsData].sort((a, b) => {
        if (a.isFavorite && !b.isFavorite) return -1;
        if (!a.isFavorite && b.isFavorite) return 1;

        return a.name.localeCompare(b.name, "es", { sensitivity: "base" });
      });

      const accountsWithoutBalances: UserAccount[] = sortedAccounts.map(
        (account) => ({
          ...account,
          balances: undefined,
          unifyState: "NOT_MIRRORED",
          mirroredAccount: false,
        }),
      );

      dispatch(updateAccounts(accountsWithoutBalances));

      if (!accountSelected && accountsWithoutBalances.length > 0) {
        let accountToSelect: UserAccount | undefined;

        if (accountId) {
          const accountFromUrl = accountsWithoutBalances.find(
            (account) => account.number === accountId,
          );
          if (accountFromUrl) {
            accountToSelect = accountFromUrl;
          }
        }

        if (!accountToSelect) {
          const favorite = getFavoriteAccount({ email: user?.email ?? "" });

          if (favorite) {
            const favAccount = accountsWithoutBalances.find(
              (a) => a.number === favorite,
            );
            accountToSelect = favAccount
              ? favAccount
              : accountsWithoutBalances[0];
          } else {
            accountToSelect = accountsWithoutBalances[0];
          }
        }

        if (accountToSelect) {
          dispatch(updateAccountSelected(accountToSelect));
        }
      }
    }
  }, [loadingUserAccountsData, userAccountsData]);

  // Update selected account with unify data
  useEffect(() => {
    if (accountSelected && !loadingUnifyAccount && unifyAccountData) {
      const accountWithUnifyData: UserAccount = {
        ...accountSelected,
        unifyState: unifyAccountData.state ?? "NOT_MIRRORED",
        mirroredAccount: unifyAccountData.state === "MIRRORED",
      };

      dispatch(updateAccountSelected(accountWithUnifyData));
    }
  }, [accountSelected?.number, loadingUnifyAccount, unifyAccountData]);

  // Set balance ONLY for accountSelected
  useEffect(() => {
    if (accountSelected && !loadingAccountBalanceData && accountBalanceData) {
      const currencyDictionary: Record<string, Currency> = {
        Peso: "ARS",
        "Dólar Cable": "CCL_USD",
        "Dólar MEP": "MEP_USD",
      };

      const balances = accountBalanceData?.positions
        ? accountBalanceData.positions.map(
            ({
              asset,
              metadata,
              agreement_quantity,
              settlement_quantity,
              available_quantity,
            }) => ({
              currency:
                currencyDictionary[
                  asset?.name as keyof typeof currencyDictionary
                ],
              balancesTypes: [
                {
                  type: "COMMITTED" as BalanceType,
                  agreement_quantity,
                  settlement_quantity,
                  available_quantity,
                },
                ...(metadata?.balances
                  ?.filter((balance) => balance.type !== "COMMITTED")
                  .map((balance) => ({
                    type: balance.type as BalanceType,
                    agreement_quantity: balance.agreement_quantity,
                    settlement_quantity: balance.available_quantity,
                    available_quantity: balance.available_quantity,
                  })) ?? []),
              ],
            }),
          )
        : undefined;

      const accountWithBalance: UserAccount = {
        ...accountSelected,
        balances,
      };

      dispatch(updateAccountSelected(accountWithBalance));
    }
  }, [accountSelected?.number, loadingAccountBalanceData, accountBalanceData]);

  useEffect(() => {
    if (
      accountSelected &&
      (!accountId || accountId !== accountSelected.number)
    ) {
      const newParams = new URLSearchParams(search);
      newParams.set("accountId", accountSelected.number);
      navigate(
        {
          pathname,
          search: newParams.toString(),
          hash,
        },
        { replace: true },
      );
    }
  }, [accountSelected, accountId]);

  useEffect(() => {
    if (user && auth?.access_token) {
      dispatch(checkAllariaMasLinked());
    }
  }, [user, auth?.access_token, dispatch]);
};

export default useAccountData;
